// Pick a Task at the asset. Pure: the asset, the tasks and this QEW's standing in, the page out.
// Words: QEW_DASHBOARD_WORDS.md "At the asset: Pick a Task" (approved 2026-10-06) and
// JSP_ADJUSTMENTS.md items 1 to 4 (approved 2026-10-08). Mockup: MOCKUP_JSP_APPROVED.html, asset page.
// Shared by the asset page, the server steps and the unit tests. Nothing here reads or writes the database.
//
// Rules:
// * The live asset page is the baseline. Only the Tier 1, 2, 3 pills come off.
//   Asset Activity, Authorization Readiness, Select Task and Start JSP / Permit stay.
// * One dropdown, grouped by tier, only the tasks switched on.
// * Current: green, Start JSP / Permit gold at once. Lapsing: amber, Start gold, Review SOP To Renew.
//   Never authorized or lapsed: Review SOP, Start grey until the SOP is acknowledged.
// * The SOP opens as the same file as Resources, with its own minimum read, section checkpoints
//   and speed check. Its acknowledgment button reads "Done. I Understand This SOP" (item 4).
// * The acknowledgment is the only way to qualify. It writes taskAuthorizations.
// * A demo asset opens the SOP for practice. It never authorizes a task.
// * The date runs from the later of the last SOP review or the last time performed,
//   plus the Task Authorization Cycle (1,095 days cap). The last 30 days are amber.
// * The read time and the speed flag are recorded. They never block (same as the live app).

import { daysBetween } from "./qew-training";
import { TASK_LAPSING_DAYS, TIER_SUB, cycleDays, type TaskAuthorization, type TaskDoc } from "./my-tasks";

export const SOP_DONE_LABEL = "Done. I Understand This SOP";
/** The live SOP files' own button. Swapped for SOP_DONE_LABEL when the file opens to qualify. */
export const SOP_FILE_LABEL = "Acknowledge &amp; Continue";
export const SOP_READ_LINE = "Read to the end to confirm. Section checkpoints and a minimum read time apply.";
export const AUTHORIZED_BY = "QEW self-authorized (SOP review)";
export const PRACTICE_LINE = "Demo asset. The SOP opens for practice. It does not qualify you on a task.";
export const START_LOCKED_LINE = "Unlocks when the SOP is reviewed.";
export const PICK_FIRST_LINE = "Pick a task to start.";
export const START_LABEL = "Start JSP / Permit";

/** One SOP the page can open, from sops/{sopNumber}. */
export interface PickSop {
  sopNumber: string;
  fileUrl: string;
  version: string;
  minReadSeconds: number;
}

export type CardState = "current" | "lapsing" | "needsReview" | "practice" | "noSop";

export interface TaskCard {
  taskId: string;
  name: string;
  tier: number;
  /** "Tier 2 · SOP-28.6". */
  meta: string;
  state: CardState;
  tone: "green" | "amber" | "grey";
  line: string;
  /** The smaller line under it, or "". */
  sub: string;
  /** The button inside the card, or null. */
  review: "Review SOP" | "Review SOP To Renew" | null;
  /** The review shows as a small link under the card, not a big button (QEW Page Return Path, 2026-10-09). */
  reviewSmall: boolean;
  /** True when Start JSP / Permit is gold. */
  startOpen: boolean;
}

export interface PickGroup {
  tier: 1 | 2 | 3;
  label: string;
  tasks: { taskId: string; name: string }[];
}

function us(iso: string): string {
  const [y, m, d] = iso.split("-");
  return y && m && d ? `${m}/${d}/${y}` : iso;
}

/** 2026-10-08 plus n days, in calendar days. */
export function addDaysIso(iso: string, n: number): string {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** "Tier 1 · Observation", the dropdown group label. Same words as My Tasks. */
export function groupLabel(tier: 1 | 2 | 3): string {
  return `Tier ${tier} · ${TIER_SUB[tier]}`;
}

/** The dropdown: tasks switched on, grouped Tier 1, 2, 3, in task ID order. Empty tiers drop out. */
export function pickGroups(tasks: readonly TaskDoc[]): PickGroup[] {
  return ([1, 2, 3] as const)
    .map((tier) => ({
      tier,
      label: groupLabel(tier),
      tasks: tasks
        .filter((t) => t.isActive && t.tier === tier)
        .sort((a, b) => a.taskId.localeCompare(b.taskId, undefined, { numeric: true }))
        .map((t) => ({ taskId: t.taskId, name: t.name })),
    }))
    .filter((g) => g.tasks.length > 0);
}

/**
 * The card for one picked task. practiceRead is true once the QEW finished a practice read on a
 * demo asset this visit. It opens Start for the demo JSP and writes nothing to his task record.
 */
export function taskCardOf(
  task: TaskDoc,
  auth: TaskAuthorization | undefined,
  sop: PickSop | null,
  cycleSetting: unknown,
  today: string,
  isDemo: boolean,
  practiceRead = false,
): TaskCard {
  const base = { taskId: task.taskId, name: task.name, tier: task.tier, meta: `Tier ${task.tier} · ${task.sopNumber ?? "No SOP"}` };
  if (!sop) {
    return {
      ...base,
      state: "noSop",
      tone: "grey",
      line: `No SOP has been authored for this task yet. Contact your ESP Manager for guidance before performing ${task.name}.`,
      sub: "",
      review: null,
      reviewSmall: false,
      startOpen: false,
    };
  }
  if (isDemo) {
    return {
      ...base,
      state: "practice",
      tone: practiceRead ? "green" : "grey",
      line: practiceRead ? "Practice read done." : PRACTICE_LINE,
      sub: practiceRead ? PRACTICE_LINE : "",
      review: "Review SOP",
      reviewSmall: practiceRead,
      startOpen: practiceRead,
    };
  }
  const cycle = cycleDays(cycleSetting);
  const reviewed = auth?.lastSopReviewOn ?? null;
  const performed = auth?.lastPerformedOn ?? null;
  const last = [reviewed, performed].filter((d): d is string => Boolean(d)).sort().pop() ?? null;
  const age = last ? daysBetween(last, today) : Number.POSITIVE_INFINITY;
  if (!last || age > cycle) {
    return { ...base, state: "needsReview", tone: "grey", line: "SOP review and confirmation required for task qualification.", sub: "", review: "Review SOP", reviewSmall: false, startOpen: false };
  }
  const through = addDaysIso(last, cycle);
  const sub = reviewed ? `Last SOP review ${us(reviewed)}.` : `Last performed ${us(performed!)}.`;
  if (age <= cycle - TASK_LAPSING_DAYS) {
    return { ...base, state: "current", tone: "green", line: `Task qualified through ${us(through)}.`, sub, review: "Review SOP", reviewSmall: true, startOpen: true };
  }
  const left = daysBetween(today, through);
  return {
    ...base,
    state: "lapsing",
    tone: "amber",
    line: `Task qualification ends in ${left} ${left === 1 ? "day" : "days"}. Review the SOP to renew.`,
    sub,
    review: "Review SOP To Renew",
    reviewSmall: false,
    startOpen: true,
  };
}

/** The asset line under the ID: "Motor Control Center · 480V (P-P) · ER2 · Springfield, KY". Blank parts drop out. */
export function assetLine(parts: { equipmentType?: string; voltage?: string; area?: string; siteName?: string }): string {
  return [parts.equipmentType, parts.voltage, parts.area, parts.siteName]
    .map((p) => (p ?? "").trim())
    .filter((p) => p && p !== "—")
    .join(" · ");
}

/** The area from the asset ID: 01-ER2-MCC-1A-B1 -> ER2. */
export function areaOf(assetId: string): string {
  return assetId.split("-")[1] ?? "";
}

/** Two letters for the avatar. "Sean Terry" -> "ST". */
export function initialsOf(name: string): string {
  const s = name
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0] ?? "")
    .join("")
    .toUpperCase()
    .slice(0, 2);
  return s || "QW";
}

/**
 * The SOP file for a qualifying read. The file is used as it is. Only the acknowledgment
 * button's words change, to the approved "Done. I Understand This SOP". A file without that
 * button shows its own words, which is safe.
 */
export function qualifyingHtml(html: string): string {
  return html.replace(`id="ackButton" disabled>${SOP_FILE_LABEL}</button>`, `id="ackButton" disabled>${SOP_DONE_LABEL}</button>`);
}

/** What the SOP file posts when the QEW acknowledges it. */
export interface SopAck {
  sopId: string;
  sopVersion: string;
  readDurationSeconds: number;
  readCheckpoints: string[];
  cheatFlagRaised: boolean;
}

/** The acknowledgment out of a window message, or null for any other message. */
export function sopAckOf(data: unknown): SopAck | null {
  if (!data || typeof data !== "object") return null;
  const d = data as Record<string, unknown>;
  if (d.event !== "sop_acknowledged" || typeof d.sopId !== "string") return null;
  const secs = Number(d.readDurationSeconds);
  return {
    sopId: d.sopId,
    sopVersion: typeof d.sopVersion === "string" ? d.sopVersion : "",
    readDurationSeconds: Number.isFinite(secs) && secs >= 0 ? Math.round(secs) : 0,
    readCheckpoints: Array.isArray(d.readCheckpoints) ? d.readCheckpoints.filter((c): c is string => typeof c === "string").slice(0, 20) : [],
    cheatFlagRaised: d.cheatFlagRaised === true,
  };
}

/** What the page sends the server: the acknowledgment in the SOP file's own message shape, so sopAckOf reads it. */
export function sopAckPayload(ack: SopAck): Record<string, unknown> {
  return { event: "sop_acknowledged", ...ack };
}

/** The server's check on an acknowledgment. Null when it can be recorded. */
export function sopAckProblem(task: TaskDoc | null, sop: PickSop | null, ack: SopAck | null): string | null {
  if (!task) return "That task is not in the program.";
  if (!task.isActive) return `${task.name} is switched off. Pick another task.`;
  if (!sop || !task.sopNumber) return `No SOP has been authored for ${task.name} yet.`;
  if (!ack) return "The SOP was not acknowledged.";
  if (ack.sopId !== task.sopNumber) return `That SOP is not ${task.sopNumber}. Open the SOP from the task again.`;
  if (ack.readDurationSeconds < sop.minReadSeconds) return "Read the SOP to the end first.";
  return null;
}

/** taskAuthorizations/{qewId}_{taskId}. One QEW, one task. */
export function authorizationId(qewId: string, taskId: string): string {
  return `${qewId}_${taskId}`;
}

/** The task record after an SOP acknowledgment. Keeps lastPerformedOn and the first date. */
export function authorizationAfterReview(
  existing: Record<string, unknown> | null,
  who: { badgeId: string; holderName: string },
  task: TaskDoc,
  ack: SopAck,
  today: string,
  at: string,
): Record<string, unknown> {
  const count = typeof existing?.sopReviewCount === "number" ? existing.sopReviewCount : 0;
  return {
    qewId: who.badgeId,
    holderName: who.holderName,
    taskId: task.taskId,
    taskName: task.name,
    tier: task.tier,
    sopNumber: task.sopNumber ?? null,
    firstAuthorizedOn: typeof existing?.firstAuthorizedOn === "string" ? existing.firstAuthorizedOn : today,
    authorizedBy: AUTHORIZED_BY,
    lastSopReviewOn: today,
    lastSopVersion: ack.sopVersion,
    sopReviewCount: count + 1,
    lastPerformedOn: typeof existing?.lastPerformedOn === "string" ? existing.lastPerformedOn : null,
    updatedAt: at,
  };
}

/** One sopReviews record. Every acknowledgment, practice reads too. Badge and name as text. */
export function sopReviewRecord(
  who: { badgeId: string; holderName: string },
  assetId: string,
  task: TaskDoc,
  ack: SopAck,
  practice: boolean,
  today: string,
  at: string,
): Record<string, unknown> {
  return {
    qewId: who.badgeId,
    holderName: who.holderName,
    siteNumber: who.badgeId.slice(0, 2),
    assetId,
    taskId: task.taskId,
    taskName: task.name,
    tier: task.tier,
    sopNumber: task.sopNumber ?? null,
    sopVersion: ack.sopVersion,
    readSeconds: ack.readDurationSeconds,
    readCheckpoints: ack.readCheckpoints,
    cheatFlagRaised: ack.cheatFlagRaised,
    practice,
    countsForTask: !practice,
    nameConfirmed: true,
    reviewedOn: today,
    reviewedAt: at,
  };
}

/** Asset Activity, as the live card. A count Firebase does not hold yet shows the blank dash. */
export interface AssetActivity {
  scans: number | null;
  jspsCompleted: number | null;
  daysSincePm: number | null;
  openWorkOrders: number | null;
}

export function activityCells(a: AssetActivity): { value: string; label: string; amber: boolean }[] {
  const v = (n: number | null) => (n === null ? "—" : String(n));
  return [
    { value: v(a.scans), label: "QR scans", amber: false },
    { value: v(a.jspsCompleted), label: "JSPs completed", amber: false },
    { value: v(a.daysSincePm), label: "Days since PM", amber: false },
    { value: v(a.openWorkOrders), label: "Open WOs", amber: (a.openWorkOrders ?? 0) > 0 },
  ];
}
